import { ChangeDetectionStrategy, Component, Injector, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { CoreParameterModel, ILightWeightObject } from '@fi-shared/models';
import { ClusterService } from '../cluster.service';
import { IClusterModel } from '../cluster.model';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { IItemsListAction2, IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { FiDisplayType } from '@fi-shared/types/display-type';
import { ObjectTypes } from '@fi-shared/types';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { IPreviewImageCellValue } from '@fi-shared/fi-wrapper-table/fi-wrapper-table';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { BusinessUnitService } from '@fi-app/business-unit/business-unit.service';
import { UserService } from '@fi-app/user/user.service';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { getSystemObjectColorClass } from '@fi-shared/utils/getSystemObjectColorClass';

@Component({
    selector: 'fi-cluster-list',
    templateUrl: './cluster-list.component.html',
    styleUrls: ['./cluster-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-cluster-list' },
    standalone: false,
})
export class ClusterListComponent extends FiRemoteStateFullDataSource<IClusterModel> implements OnInit {
    private clusterService = inject(ClusterService);
    private stateAccessCheckService = inject(StateAccessCheckService);
    private statisticService = inject(StatisticService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    private businessUnitService = inject(BusinessUnitService);
    private userService = inject(UserService);

    public defaultSortColumnName: string = 'Name';
    public defaultSortDirection: boolean = false;
    public objectType: ObjectTypes = ObjectTypes.Cluster;
    public itemsListType = FiItemsListType.Clusters;

    protected readonly clusterAccessRight: boolean = this.stateAccessCheckService.isStateAllowedForCurrentUser(
        FiStateNames.Cluster.Add,
    );
    protected readonly itemsListActions: IItemsListAction2[] = [
        {
            title: this.appTextService.get('common.delete'),
            onClick: () => this.confirmDeleteSelectedClusters(),
        },
    ];
    protected currentListDisplayType: FiDisplayType;

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        this.setCurrentListDisplayType();
        this.statisticService.Visit(ModuleType.Core);
        this.subscribeOnListDisplayTypeChange();
    }

    private confirmDeleteSelectedClusters(): void {
        this.fiItemsDeleteHelperService.commonDeleteConfirm(ObjectTypes.Cluster, this.selectedItems()).then(() => {
            this.deleteSelectedClusters();
        });
    }

    private deleteSelectedClusters(): void {
        const selectedItems = this.selectedItems();
        const selectedItemIds: number[] = selectedItems.map((selectedItem: IClusterModel) => {
            return selectedItem.Id;
        });
        this.clusterService.deleteClusters(selectedItemIds).then(() => {
            this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                ObjectTypes.Cluster,
                selectedItemIds,
                selectedItems[0].Name,
            );
            this.reloadData();
        });
    }

    protected getClusterBusinessUnits(cluster: IClusterModel): INameIdObj[] {
        return cluster.BusinessUnits.map((businessUnit: ILightWeightObject) => {
            return { Name: businessUnit.Name, Id: businessUnit.Id };
        });
    }

    protected openBusinessUnitProfileDialog(businessUnitId: number): void {
        this.businessUnitService.openBusinessUnitViewDialog(businessUnitId);
    }

    protected openUserViewDialog($event: MouseEvent, userId: number): void {
        $event.stopPropagation();
        this.userService.openUserViewDialog(userId);
    }

    public loadListDataFn(loadArgs: ILoadDataArgs): IFiHttpPromise<IItemsListDataFetchResult<IClusterModel>> {
        let parameter: CoreParameterModel = new CoreParameterModel();
        parameter = Object.assign(parameter, this.getItemsListParameter(loadArgs));
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };

        return this.clusterService.getClustersListData(parameter);
    }

    protected getClusterImageCellValue(): IPreviewImageCellValue {
        return {
            image: '',
            imageLarge: '',
            iconClass: 'fi-icon-cluster-ico',
        };
    }

    protected goToClusterViewer(itemId: number, $event: MouseEvent): void {
        $event.stopPropagation();
        this.fiStateService.go(FiStateNames.Cluster.View, { Id: itemId });
    }

    protected readonly getSystemObjectColorClass = getSystemObjectColorClass;
}
